import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import AuthBrandPanel from "components/Auth/AuthBrandPanel";
import AuthField from "components/Auth/AuthField";
import { InlineFormMessage } from "components/InlineFeedback/InlineFeedback";
import eyeIcon from "assets/images/auth/eye-icon.svg";
import verificationMailIcon from "assets/images/auth/verification-mail.svg";
import arrowLeftIcon from "assets/images/auth/arrow-left.svg";
import { sendOTPToUser, userResendOTP, validateOTP } from "utils/authApi";
import { showResMsg } from "utils/generic";
import "styles/auth.css";

const SESSION_STORAGE_KEY = "inr_pending_signup";

const initialValues = {
  fullName: "",
  firstName: "",
  lastName: "",
  emailAddress: "",
  newPassword: "",
  confirmPassword: "",
  emailOTP: "",
};

function splitFullName(fullName) {
  const parts = fullName.trim().split(/\s+/);
  return { firstName: parts.shift() || "", lastName: parts.join(" ") || "-" };
}

function BuSignUp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Restore existing session state if user refreshed on the OTP page
  const savedSession = (() => {
    try {
      const item = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  })();

  const [values, setValues] = useState(savedSession?.values || initialValues);
  const [errors, setErrors] = useState({});
  const [accepted, setAccepted] = useState(Boolean(savedSession));
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(
    savedSession?.step === "otp" ? "otp" : "signup"
  );

  const [isSubmittingSignup, setIsSubmittingSignup] = useState(false);
  const [signupError, setSignupError] = useState("");
  // OTP state
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(
    savedSession?.step === "otp" ? 30 : 0
  );
  const inputRefs = useRef([]);

  // Auto-focus first input when entering OTP step
  useEffect(() => {
    if (step === "otp") {
      const timer = setTimeout(() => {
        const firstEmptyIndex = otp.findIndex((d) => !d);
        const focusIndex = firstEmptyIndex === -1 ? 0 : firstEmptyIndex;
        inputRefs.current[focusIndex]?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [step]);

  // Resend cooldown timer countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const updateValue = (name) => (event) => {
    setValues((current) => ({ ...current, [name]: event.target.value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validateSignup = () => {
    const next = {};
    if (values.fullName.trim().length < 2)
      next.fullName = "Please enter your full name";
    if (!/^\S+@\S+\.\S+$/.test(values.emailAddress.trim()))
      next.emailAddress = "Please enter a valid email address";
    if (values.newPassword.length < 8 || values.newPassword.length > 20)
      next.newPassword = "Password must be between 8 and 20 characters";
    else if (!/[A-Z]/.test(values.newPassword))
      next.newPassword = "Include at least one uppercase letter";
    else if (!/[!@#$%^&*(),.?":{}|<>]/.test(values.newPassword))
      next.newPassword = "Include at least one special character";
    if (!accepted)
      next.accepted = "Please accept the Terms of Service and Privacy Policy";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const createAccount = (event) => {
    event.preventDefault();
    setSignupError("");
    if (!validateSignup()) return;

    setIsSubmittingSignup(true);
    const payload = {
      ...values,
      ...splitFullName(values.fullName),
      confirmPassword: values.newPassword,
      userPassword: values.newPassword,
    };
    setValues(payload);

    sendOTPToUser(
      payload,
      (data) => {
        setIsSubmittingSignup(false);
        if (!data.Result_Status) {
          setSignupError(
            data.Result_Message || "Failed to create account. Please try again."
          );
          return;
        }
        try {
          sessionStorage.setItem(
            SESSION_STORAGE_KEY,
            JSON.stringify({ values: payload, step: "otp" })
          );
        } catch {
          // ignore storage errors
        }
        setOtp(["", "", "", "", "", ""]);
        setOtpError("");
        setResendCooldown(30);
        setStep("otp");
      },
      dispatch
    );
  };

  // Handle single digit input
  const handleOtpChange = (index, event) => {
    const val = event.target.value;
    const cleanDigit = val.replace(/\D/g, "").slice(-1);

    const newOtp = [...otp];
    newOtp[index] = cleanDigit;
    setOtp(newOtp);
    setOtpError("");

    if (cleanDigit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace and arrow navigation
  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste of 6-digit code
  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text/plain");
    const digits = pasteData.replace(/\D/g, "").slice(0, 6).split("");

    if (digits.length > 0) {
      const newOtp = [...otp];
      digits.forEach((digit, idx) => {
        if (idx < 6) newOtp[idx] = digit;
      });
      setOtp(newOtp);
      setOtpError("");

      const focusIndex = Math.min(digits.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const resendCode = () => {
    if (resendCooldown > 0) return;
    userResendOTP(
      { emailAddress: values.emailAddress, sendOtpTo: ["Email"] },
      "login/resendOTP",
      (data) => {
        if (data.Result_Status) {
          setResendCooldown(30);
          setOtp(["", "", "", "", "", ""]);
          setOtpError("");
          inputRefs.current[0]?.focus();
          showResMsg("Verification code sent successfully", 2000, true);
        } else {
          setOtpError(data.Result_Message || "Failed to resend code.");
          showResMsg(data.Result_Message, 2000, false);
        }
      },
      dispatch
    );
  };

  const verifyOtpCode = (event) => {
    if (event) event.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) {
      setOtpError("Please enter the complete 6-digit verification code");
      return;
    }

    setIsSubmittingOtp(true);
    setOtpError("");

    validateOTP(
      { emailAddress: values.emailAddress, emailOTP: code },
      "login/validateOTP",
      (data) => {
        setIsSubmittingOtp(false);
        if (!data.Result_Status) {
          setOtpError(data.Result_Message || "Invalid verification code");
          return;
        }

        try {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        } catch {
          // ignore
        }

        showResMsg("Email verified successfully", 1500, true);
        const destinationUserId =
          data.Result_Output?._id || data.Result_Output?.businessUserID;
        if (destinationUserId) {
          navigate(`/wizard/${destinationUserId}`, { replace: true });
        } else {
          navigate("/login", { replace: true });
        }
      },
      dispatch
    );
  };

  const handleChangeEmail = () => {
    setStep("signup");
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({ values, step: "signup" })
      );
    } catch {
      // ignore
    }
  };

  const isOtpComplete = otp.every((d) => d !== "");

  return (
    <main
      className={`auth-page auth-page--signup${
        step === "otp" ? " auth-page--email-verification" : ""
      }`}
    >
      <AuthBrandPanel />
      <section
        className={`auth-panel auth-panel--signup${
          step === "otp" ? " auth-panel--email-verification" : ""
        }`}
      >
        {step === "signup" ? (
          <form
            className="auth-signup-form"
            onSubmit={createAccount}
            noValidate
          >
            <header className="auth-title auth-signup-title">
              <h2>Create your account</h2>
              <p>Start your free Silver plan today</p>
            </header>
            {signupError && (
              <InlineFormMessage
                message={signupError}
                type="error"
                onClose={() => setSignupError("")}
              />
            )}
            <div className="auth-signup-inputs">
              <AuthField
                id="fullName"
                label="Full name"
                required
                error={errors.fullName}
              >
                <input
                  id="fullName"
                  className={`auth-control${
                    errors.fullName ? " auth-control--error" : ""
                  }`}
                  value={values.fullName}
                  onChange={updateValue("fullName")}
                  placeholder="Alex Smith"
                  autoComplete="name"
                  maxLength={51}
                />
              </AuthField>
              <AuthField
                id="signupEmail"
                label="Email address"
                required
                error={errors.emailAddress}
              >
                <input
                  id="signupEmail"
                  className={`auth-control${
                    errors.emailAddress ? " auth-control--error" : ""
                  }`}
                  type="email"
                  value={values.emailAddress}
                  onChange={updateValue("emailAddress")}
                  placeholder="alex@invoice-recover.com.au"
                  autoComplete="email"
                  maxLength={50}
                />
              </AuthField>
              <AuthField
                id="signupPassword"
                label="Password"
                required
                error={errors.newPassword}
              >
                <div className="auth-password">
                  <input
                    id="signupPassword"
                    className={`auth-control${
                      errors.newPassword ? " auth-control--error" : ""
                    }`}
                    type={showPassword ? "text" : "password"}
                    value={values.newPassword}
                    onChange={updateValue("newPassword")}
                    placeholder="Enter password"
                    autoComplete="new-password"
                    maxLength={20}
                  />
                  <button
                    className="auth-password__toggle"
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    <img src={eyeIcon} alt="" />
                  </button>
                </div>
                {!errors.newPassword && (
                  <span className="auth-field__hint">
                    Must be at least 8 characters
                  </span>
                )}
              </AuthField>
              <label className="auth-terms-check">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(event) => {
                    setAccepted(event.target.checked);
                    setErrors((current) => ({ ...current, accepted: "" }));
                  }}
                />
                <span>
                  I agree to the <a href="#terms">Terms of Service</a> and{" "}
                  <a href="#privacy">Privacy Policy</a>
                </span>
              </label>
              {errors.accepted && (
                <span className="auth-field__error" role="alert">
                  {errors.accepted}
                </span>
              )}
            </div>
            <button
              className={`auth-button auth-button--primary${
                isSubmittingSignup ? " auth-button--loading" : ""
              }`}
              type="submit"
              disabled={isSubmittingSignup}
            >
              {isSubmittingSignup && <span className="auth-button__spinner" />}
              <span>
                {isSubmittingSignup ? "Creating account..." : "Create account"}
              </span>
            </button>
          </form>
        ) : (
          <div className="auth-email-verification">
            <div className="auth-email-verification__icon">
              <img src={verificationMailIcon} alt="" />
            </div>
            <header className="auth-email-verification__title">
              <h2>Verify your email</h2>
              <p>
                We&apos;ve sent a 6-digit code to{" "}
                <strong>{values.emailAddress}</strong>. Enter it below to verify
                your email.
              </p>
            </header>

            <form className="auth-otp-form" onSubmit={verifyOtpCode} noValidate>
              <div className="auth-otp-field">
                <label className="auth-otp-label">Verification code</label>
                <div className="auth-otp-inputs" onPaste={handleOtpPaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      className={`auth-otp-box${
                        digit ? " auth-otp-box--filled" : ""
                      }${otpError ? " auth-otp-box--error" : ""}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      pattern="[0-9]*"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      aria-label={`Digit ${index + 1}`}
                    />
                  ))}
                </div>
                {otpError && (
                  <div className="auth-otp-error-msg" role="alert">
                    {otpError}
                  </div>
                )}
              </div>

              <button
                className={`auth-button auth-button--primary${
                  isSubmittingOtp ? " auth-button--loading" : ""
                }`}
                type="submit"
                disabled={isSubmittingOtp || !isOtpComplete}
              >
                {isSubmittingOtp && <span className="auth-button__spinner" />}
                <span>{isSubmittingOtp ? "Verifying..." : "Verify email"}</span>
              </button>
            </form>

            <div className="auth-otp-info">
              <p>Didn&apos;t receive the code? Check your spam folder.</p>
              <div className="auth-otp-resend">
                {resendCooldown > 0 ? (
                  <span className="auth-otp-resend--disabled">
                    Resend code in 00:{String(resendCooldown).padStart(2, "0")}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="auth-otp-resend-btn"
                    onClick={resendCode}
                  >
                    Resend code
                  </button>
                )}
              </div>
              <button
                type="button"
                className="auth-otp-back-btn"
                onClick={handleChangeEmail}
              >
                <img src={arrowLeftIcon} alt="" />
                Change email
              </button>
            </div>
          </div>
        )}
        {step === "signup" && (
          <p className="auth-signup-login">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        )}
        <footer className="auth-legal">
          <a href="#privacy">Privacy</a>
          <span>·</span>
          <a href="#terms">Terms</a>
        </footer>
      </section>
    </main>
  );
}

export default BuSignUp;
